import { useCallback, useEffect, useRef, useState } from 'react'
import { entities } from '../../../../constants/entities'
import useRemoteSelectOptions from '../../../../hooks/useRemoteSelectOptions'
import useSelectOptions from '../../../../hooks/useSelectOptions'
import { useSectionFields } from '../../../../hooks/useSectionFields'
import useQueryWithLoader from '../../../../hooks/useQueryWithLoader'

export const useBillPageLogic = ({
  mode = 'create',
  billingFields,
  setBillingFields,
  formData,
  setFormData,
  resetForm,
  baseServiceGet,
  initialBillingData = null,
}) => {
  const { updateField, removeFields } = useSectionFields()

  const billTypeOptions = [
    { value: 'by_contract', label: 'Faturar por contrato' },
    { value: 'separate', label: 'Faturar avulso' },
  ]

  const billTypeSelect = useSelectOptions({
    fieldId: 'billing.bill_type',
    formData,
    setFormData,
    rawOptions: billTypeOptions,
    mapToOption: (item) => ({
      value: item.value,
      label: item.label,
    }),
  })

  const organizationSelect = useRemoteSelectOptions({
    queryKey: ['organizations'],
    baseServiceGet,
    columnDataBase: ['id', 'alias'],
    entityPath: entities.organizations.get,
    fieldId: 'billing.organization_id',
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    immediateFetch: mode === 'edit',
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias}`,
    }),
  })

  const customerSelect = useRemoteSelectOptions({
    queryKey: ['customers'],
    baseServiceGet,
    columnDataBase: ['id', 'alias'],
    entityPath: entities.customers.withRentFlow.get,
    fieldId: 'billing.customer_id',
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    immediateFetch: mode === 'edit',
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias}`,
    }),
  })

  const customerRef = useRef(customerSelect.selectedValue?.value)

  const contractSelect = useRemoteSelectOptions({
    queryKey: ['contractsByCustomer'],
    baseServiceGet,
    columnDataBase: ['id', 'number', 'name'],
    entityPath: entities.contracts.get,
    extraFilters: {
      organization_id: organizationSelect.selectedValue?.value,
      $or: [
        { customer_id: { $eq: customerSelect.selectedValue?.value } },
        {
          'recipientCustomers.customer_id': {
            $eq: customerSelect.selectedValue?.value,
          },
        },
      ],
    },
    defaultSort: { column: 'id', order: 'desc' },
    immediateFetch: mode === 'edit',
    enabled: !!customerSelect.selectedValue?.value,
    fieldId: 'billing.contract_id',
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchWhen: () => {
      if (customerRef.current !== customerSelect.selectedValue?.value) {
        customerRef.current = customerSelect.selectedValue?.value
        return true
      }
      return false
    },
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.number} - ${item.name}`,
    }),
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selects = {
    'billing.bill_type': billTypeSelect,
    'billing.organization_id': organizationSelect,
    'billing.customer_id': customerSelect,
    'billing.contract_id': contractSelect,
  }

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.options || []
    },
    [selects]
  )

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [selects]
  )

  // Buscar informações do valor do contrato e saldo do contrato
  const { data: contractAmountResponse } = useQueryWithLoader({
    queryKey: ['contractAmountDetails', contractSelect.selectedValue?.value],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.contracts.contractAmount(contractSelect.selectedValue?.value),
        {
          deleted_at: false,
        }
      )
      return response.result
    },
    enabled: !!contractSelect.selectedValue?.value,
  })

  const [originalBillingFields] = useState(billingFields)

  useEffect(() => {
    let updatedBillingFields = [...originalBillingFields]

    if (mode === 'create') {
      const allFields = originalBillingFields.filter(
        (item) =>
          item.id !== 'billing.bill_type' &&
          item.id !== 'billing.organization_id'
      )

      // * Se o modo for por contrato
      if (billTypeSelect.selectedValue?.value === 'by_contract') {
        updatedBillingFields = updateField({
          field_id: ['billing.customer_id'],
          arrayDependente: originalBillingFields,
          updates: {
            disabled: !organizationSelect.selectedValue?.value,
          },
        })

        updatedBillingFields = updateField({
          field_id: [
            'billing.contract_id',
            'billing.number',
            'billing.start_date',
            'billing.end_date',
          ],
          arrayDependente: updatedBillingFields,
          updates: {
            disabled: !customerSelect.selectedValue?.value,
          },
        })
      } else if (billTypeSelect.selectedValue?.value === 'separate') {
        // Cliente só fica disponível após selecionar organização
        updatedBillingFields = updateField({
          field_id: ['billing.customer_id'],
          arrayDependente: originalBillingFields,
          updates: {
            disabled: !organizationSelect.selectedValue?.value,
            fullWidth: true,
          },
        })

        // Remover o campo de contrato
        updatedBillingFields = removeFields({
          field_id: ['billing.contract_id'],
          arrayDependente: updatedBillingFields,
        })

        // Campos dependentes do cliente
        updatedBillingFields = updateField({
          field_id: [
            'billing.number',
            'billing.start_date',
            'billing.end_date',
          ],
          arrayDependente: updatedBillingFields,
          updates: {
            disabled: !customerSelect.selectedValue?.value,
          },
        })
      } else {
        updatedBillingFields = removeFields({
          field_id: allFields.map((item) => item.id),
          arrayDependente: originalBillingFields,
        })
      }
    }

    setBillingFields(updatedBillingFields)
  }, [
    mode,
    billTypeSelect.selectedValue?.value,
    organizationSelect.selectedValue?.value,
    customerSelect.selectedValue?.value,
    originalBillingFields,
    removeFields,
    setBillingFields,
    updateField,
  ])

  useEffect(() => {
    if (mode === 'edit' && initialBillingData) {
      setFormData((prev) => ({
        ...prev,
        billing: {
          ...prev.billing,
          ...initialBillingData,
        },
      }))
    }
  }, [mode, initialBillingData, setFormData])

  // * Limpar formulário quando o cliente ou o tipo de faturamento mudar
  const previousValues = useRef({
    billType: billTypeSelect.selectedValue?.value,
    customer: customerSelect.selectedValue?.value,
  })

  useEffect(() => {
    const currentBillType = billTypeSelect.selectedValue?.value
    const currentCustomer = customerSelect.selectedValue?.value

    const billTypeChanged =
      previousValues.current.billType &&
      previousValues.current.billType !== currentBillType

    const customerChanged =
      previousValues.current.customer &&
      previousValues.current.customer !== currentCustomer

    if (billTypeChanged || customerChanged) {
      // * campos que serão preservados
      resetForm(['billing.bill_type', 'billing.organization_id'])
    }

    previousValues.current = {
      billType: currentBillType,
      customer: currentCustomer,
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billTypeSelect.selectedValue?.value, customerSelect.selectedValue?.value])

  return {
    selects: {
      billTypeSelect,
      organizationSelect,
      customerSelect,
      contractSelect,

      getOptions,
      getSelectedValue,
      getSelectMeta,
    },
    contract: {
      id: contractSelect?.selectedValue?.value,
      recipient_customer_id: customerSelect?.selectedValue?.value,
      amount: contractAmountResponse,
    },
    separate: {
      recipient_customer_id: customerSelect?.selectedValue?.value,
    },
  }
}
